import React, { useCallback, useRef, useState } from 'react';
import {
  Link,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Warning20Filled,
  ChevronDown16Regular,
  Open16Regular,
} from '@fluentui/react-icons';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import type { EmailDedupMatch } from '../services/customerService';
import { formatBusinessDate } from '../lib/businessTime';

interface EmailDedupBannerProps {
  match: EmailDedupMatch;
  /** Extra matches beyond the primary — surfaced as "+N more" in the popover. */
  additional?: EmailDedupMatch[];
}

const useStyles = makeStyles({
  // Warning surface: white fill with a bright-orange (Pumpkin) border + icon
  // carrying the warning; body text stays neutral. Accent-not-fill is the
  // project's warning-UI language (the orange edge + icon read on white).
  banner: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    ...shorthands.padding('8px', '12px'),
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    ...shorthands.border('1px', 'solid', tokens.colorPalettePumpkinBorderActive),
    ...shorthands.borderRadius('4px'),
    fontSize: '13px',
    lineHeight: '18px',
    cursor: 'pointer',
    // Visible focus ring for keyboard users — the banner is interactive
    // (toggles a popover) and gets tabbed onto.
    '&:focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '2px',
    },
  },
  bannerIcon: {
    flexShrink: 0,
    color: tokens.colorPalettePumpkinBorderActive,
  },
  bannerText: {
    flexGrow: 1,
    minWidth: 0,
  },
  bannerCue: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground1,
    opacity: 0.8,
  },
  surface: {
    width: '300px',
    ...shorthands.padding('0'),
    ...shorthands.borderRadius('4px'),
    boxShadow: tokens.shadow8,
    backgroundColor: tokens.colorNeutralBackground1,
    // Drawer surfaces in v9 sit at z-index ~1000000; clear that so the
    // popover renders above the drawer instead of behind it.
    zIndex: 1000001,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('2px'),
    ...shorthands.padding('12px', '14px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  headerName: {
    fontSize: '15px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerEmail: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('6px'),
    ...shorthands.padding('12px', '14px'),
    fontSize: '13px',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    ...shorthands.gap('12px'),
  },
  detailLabel: {
    color: tokens.colorNeutralForeground2,
  },
  detailValue: {
    color: tokens.colorNeutralForeground1,
    textAlign: 'right',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  additionalNote: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    fontStyle: 'italic',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    ...shorthands.gap('6px'),
    ...shorthands.padding('10px', '14px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  profileLink: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('4px'),
    fontSize: '13px',
    fontWeight: 600,
  },
});

const formatDateShort = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return formatBusinessDate(iso);
};

const formatPhoneDisplay = (raw: string | null): string | null => {
  if (!raw) return null;
  const parsed = parsePhoneNumberFromString(raw);
  if (parsed && parsed.isValid()) return parsed.formatNational();
  return raw;
};

const resolveEntityKind = (m: EmailDedupMatch): 'contact' | 'company' | 'customer' => {
  if (m.company_id) return 'contact';
  if (m.legal_type === 'commercial') return 'company';
  return 'customer';
};

/**
 * Inline warning shown beneath the email field on the Add Customer drawer
 * when a normalized match exists. Click or hover opens a popover that
 * surfaces enough identity (name, phone, customer since, last order) for
 * staff to confirm "yes, this is the same person" without leaving the
 * drawer. "View full profile" opens the customer page in a new tab for the
 * ambiguous cases.
 *
 * Touch + desktop: hover-open covers desktop; click-toggle covers touch
 * (mouseenter doesn't reliably fire on tap). The chevron cue makes the
 * tap affordance discoverable.
 */
const EmailDedupBanner: React.FC<EmailDedupBannerProps> = ({ match, additional = [] }) => {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelDismiss = useCallback(() => {
    if (dismissTimer.current) {
      clearTimeout(dismissTimer.current);
      dismissTimer.current = null;
    }
  }, []);

  const scheduleDismiss = useCallback(() => {
    cancelDismiss();
    dismissTimer.current = setTimeout(() => setOpen(false), 150);
  }, [cancelDismiss]);

  const handleEnter = useCallback(() => {
    cancelDismiss();
    setOpen(true);
  }, [cancelDismiss]);

  const handleClick = useCallback(() => {
    cancelDismiss();
    setOpen((prev) => !prev);
  }, [cancelDismiss]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen((prev) => !prev);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }, []);

  const kind = resolveEntityKind(match);
  const profilePath = `/customers/${match.booqable_id || match.id}`;
  const phoneDisplay = formatPhoneDisplay(match.phone);
  const customerSince = formatDateShort(match.created_at);
  const lastOrder = formatDateShort(match.latest_order_at);
  const message =
    kind === 'company'
      ? 'An existing company already uses this email.'
      : kind === 'contact'
        ? 'An existing contact already uses this email.'
        : 'An existing customer already uses this email.';

  return (
    <Popover
      open={open}
      onOpenChange={(_, data) => setOpen(data.open)}
      positioning="below-start"
      withArrow
    >
      <PopoverTrigger disableButtonEnhancement>
        <div
          className={styles.banner}
          role="button"
          tabIndex={0}
          aria-haspopup="dialog"
          aria-expanded={open}
          onMouseEnter={handleEnter}
          onMouseLeave={scheduleDismiss}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        >
          <Warning20Filled className={styles.bannerIcon} />
          <span className={styles.bannerText}>{message}</span>
          <ChevronDown16Regular className={styles.bannerCue} />
        </div>
      </PopoverTrigger>
      <PopoverSurface
        className={styles.surface}
        onMouseEnter={cancelDismiss}
        onMouseLeave={scheduleDismiss}
      >
        <div className={styles.header}>
          <span className={styles.headerName}>{match.name || 'Unnamed customer'}</span>
          <span className={styles.headerEmail}>{match.email}</span>
        </div>
        <div className={styles.body}>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Phone</span>
            <span className={styles.detailValue}>{phoneDisplay || '—'}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Customer since</span>
            <span className={styles.detailValue}>{customerSince || '—'}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Last order</span>
            <span className={styles.detailValue}>{lastOrder || 'No orders yet'}</span>
          </div>
          {additional.length > 0 && (
            <span className={styles.additionalNote}>
              +{additional.length} more customer{additional.length === 1 ? '' : 's'} share this email.
            </span>
          )}
        </div>
        <div className={styles.footer}>
          <Link
            href={profilePath}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.profileLink}
          >
            View full profile
            <Open16Regular />
          </Link>
        </div>
      </PopoverSurface>
    </Popover>
  );
};

export default EmailDedupBanner;
